import { useEffect, useMemo } from "react";
import {
  ROLE_LEVELS,
  HR_RECRUITMENT_STAGE_OPTIONS,
  HR_RECRUITMENT_STAGE_STORAGE_KEY,
  HR_ATTENDANCE_OVERRIDE_STORAGE_KEY,
  HR_ANNOUNCEMENTS_STORAGE_KEY,
  HR_DOCUMENT_UPLOADS_STORAGE_KEY
} from "../dashboard/Config";

export function HrAdminData({
  hrStaffRoster,
  hrTaskBoard,
  hrRecruitmentStageById,
  setHrRecruitmentStageById,
  hrAttendanceOverrideById,
  setHrAttendanceOverrideById,
  hrAnnouncements,
  hrDocumentUploads,
  normalizeUserStatus
}) {
  const hrEligibleStaffRoster = useMemo(
    () =>
      hrStaffRoster.filter((item) => {
        const roleKey = String(item?.role || "").trim() || "guest";
        const roleLevel = ROLE_LEVELS[roleKey] || 0;
        const hasJobTitle = Boolean(String(item?.jobTitle || "").trim());
        return roleLevel >= ROLE_LEVELS.admin && hasJobTitle;
      }),
    [hrStaffRoster]
  );

  const hrEligibleIdSet = useMemo(
    () => new Set(hrEligibleStaffRoster.map((item) => Number(item?.id || 0)).filter((id) => id > 0)),
    [hrEligibleStaffRoster]
  );

  const hrRecruitmentRows = useMemo(
    () =>
      hrTaskBoard
        .filter((item) => hrEligibleIdSet.has(Number(item?.id || 0)))
        .map((item) => {
          const rowId = Number(item?.id || 0);
          return {
            ...item,
            onboardingStage:
              String((hrRecruitmentStageById && hrRecruitmentStageById[rowId]) || item?.onboardingStage || "").trim() ||
              "needs_position"
          };
        }),
    [hrTaskBoard, hrEligibleIdSet, hrRecruitmentStageById]
  );

  const hrEmployeeRows = useMemo(
    () =>
      hrEligibleStaffRoster.map((item) => {
        const rowId = Number(item?.id || 0);
        const override = (hrAttendanceOverrideById && hrAttendanceOverrideById[rowId]) || null;
        return {
          ...item,
          status: override?.status || item?.status,
          lastActiveAt: override?.lastActiveAt || item?.lastActiveAt
        };
      }),
    [hrEligibleStaffRoster, hrAttendanceOverrideById]
  );

  useEffect(() => {
    window.localStorage.setItem(HR_RECRUITMENT_STAGE_STORAGE_KEY, JSON.stringify(hrRecruitmentStageById || {}));
  }, [hrRecruitmentStageById]);

  useEffect(() => {
    window.localStorage.setItem(HR_ATTENDANCE_OVERRIDE_STORAGE_KEY, JSON.stringify(hrAttendanceOverrideById || {}));
  }, [hrAttendanceOverrideById]);

  useEffect(() => {
    window.localStorage.setItem(HR_ANNOUNCEMENTS_STORAGE_KEY, JSON.stringify(Array.isArray(hrAnnouncements) ? hrAnnouncements : []));
  }, [hrAnnouncements]);

  useEffect(() => {
    window.localStorage.setItem(HR_DOCUMENT_UPLOADS_STORAGE_KEY, JSON.stringify(Array.isArray(hrDocumentUploads) ? hrDocumentUploads : []));
  }, [hrDocumentUploads]);

  useEffect(() => {
    setHrRecruitmentStageById((prev) => {
      const source = prev && typeof prev === "object" ? prev : {};
      const next = {};
      let changed = false;

      Object.entries(source).forEach(([key, value]) => {
        const id = Number(key);
        if (!hrEligibleIdSet.has(id)) {
          changed = true;
          return;
        }
        next[String(id)] = String(value || "").trim() || "needs_position";
      });

      if (!changed && Object.keys(next).length === Object.keys(source).length) {
        return prev;
      }
      return next;
    });
  }, [hrEligibleIdSet, setHrRecruitmentStageById]);

  useEffect(() => {
    setHrAttendanceOverrideById((prev) => {
      const source = prev && typeof prev === "object" ? prev : {};
      const next = {};
      let changed = false;

      Object.entries(source).forEach(([key, value]) => {
        const id = Number(key);
        if (!hrEligibleIdSet.has(id)) {
          changed = true;
          return;
        }

        const status = normalizeUserStatus(value?.status || "active");
        const lastActiveAt = String(value?.lastActiveAt || "").trim();
        next[String(id)] = {
          status,
          lastActiveAt
        };
      });

      if (!changed && Object.keys(next).length === Object.keys(source).length) {
        return prev;
      }
      return next;
    });
  }, [hrEligibleIdSet, normalizeUserStatus, setHrAttendanceOverrideById]);

  return {
    hrEligibleIdSet,
    hrRecruitmentRows,
    hrEmployeeRows
  };
}
